import { useNavigation } from '@react-navigation/native';
import React, {
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
} from 'react';
import { FlatList, Keyboard } from 'react-native';
import { ProgressBar } from 'react-native-paper';

import ChatImages from '@components/Chat/ChatImages';
import DocumentCard from '@components/Chat/DocumentCard';
import { Box, Text } from '@components/Restyle';
import { Document, LocalFile } from '@graphql/generated';
import { useOptionalChatUploadManager } from '@hooks/useChatUploadManager';
import theme from '@themes/theme';

export type TaskCreateAttachmentsSnapshot = {
  localFiles: LocalFile[];
  localImages: LocalFile[];
  shouldShowLoader: boolean;
  fileUploadedCount: number;
  isReadySubmitting: boolean;
};

type TaskCreateAttachmentsStore = {
  getSnapshot: () => TaskCreateAttachmentsSnapshot;
  subscribe: (onStoreChange: () => void) => () => void;
};

type TaskCreateAttachmentsState = {
  store: TaskCreateAttachmentsStore;
  setLocalFiles: React.Dispatch<React.SetStateAction<LocalFile[]>>;
  setLocalImages: React.Dispatch<React.SetStateAction<LocalFile[]>>;
};

const EMPTY_SNAPSHOT: TaskCreateAttachmentsSnapshot = {
  localFiles: [],
  localImages: [],
  shouldShowLoader: false,
  fileUploadedCount: 0,
  isReadySubmitting: false,
};
const getEmptySnapshot = () => EMPTY_SNAPSHOT;
const noopSubscribe = () => () => {};

/**
 * Builds a stable external-store handle for the attachment preview's
 * ever-changing data (TA-10495 #17 remount fix, Copilot review).
 * TaskCreateBottomSheet's renderFooter must keep a stable identity —
 * @gorhom/bottom-sheet treats a new footerComponent reference as a new
 * component type and remounts the whole footer subtree (including
 * TaskCreateModalFooter's own project-name query) whenever it changes, so
 * attachment state (which changes on every file pick and upload-progress
 * tick) can't live in its dependency array. Call this at TaskCreateBottomSheet's
 * own top level — NOT inside the Provider below, which is a descendant of
 * the memoized renderFooter closure and so would only re-render as often as
 * that closure is re-invoked. Mutating a ref here, on every real render of
 * TaskCreateBottomSheet (which happens on every localFiles/localImages
 * change regardless of renderFooter), and notifying subscribers from an
 * effect keyed on the snapshot, lets TaskCreateAttachmentsPreview update
 * itself via useSyncExternalStore independent of whether renderFooter's
 * closure is ever re-invoked again after mount.
 */
export const useTaskCreateAttachmentsStore = (
  snapshot: TaskCreateAttachmentsSnapshot
): TaskCreateAttachmentsStore => {
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  const listenersRef = useRef(new Set<() => void>());

  useEffect(() => {
    listenersRef.current.forEach((listener) => listener());
  }, [snapshot]);

  return useRef<TaskCreateAttachmentsStore>({
    getSnapshot: () => snapshotRef.current,
    subscribe: (listener) => {
      listenersRef.current.add(listener);
      return () => listenersRef.current.delete(listener);
    },
  }).current;
};

const TaskCreateAttachmentsContext =
  React.createContext<TaskCreateAttachmentsState | null>(null);

const LocalFilesListFooter = () => <Box marginRight='xs' />;
const LocalFilesListHeader = () => <Box marginLeft='m' />;

export const TaskCreateAttachmentsProvider: React.FC<
  TaskCreateAttachmentsState & { children: React.ReactNode }
> = ({ children, ...value }) => (
  <TaskCreateAttachmentsContext.Provider value={value}>
    {children}
  </TaskCreateAttachmentsContext.Provider>
);

/**
 * Attachment thumbnails/upload progress for the New Task sheet (TA-10495
 * #17) — rendered as part of the sheet's fixed footer, directly above the
 * CTA row, instead of inline in the scrolling form body where it used to
 * drift out of view depending on description length and keyboard state.
 */
export const TaskCreateAttachmentsPreview: React.FC = () => {
  const state = useContext(TaskCreateAttachmentsContext);
  const {
    localFiles,
    localImages,
    shouldShowLoader,
    fileUploadedCount,
    isReadySubmitting,
  } = useSyncExternalStore(
    state?.store.subscribe ?? noopSubscribe,
    state?.store.getSnapshot ?? getEmptySnapshot
  );
  const navigation = useNavigation();

  // The throwing `useChatUploadManager` would crash before the guard below
  // ever ran, so "renders nothing without a Provider" only holds if this one
  // is optional too (Copilot review).
  const uploadManager = useOptionalChatUploadManager();

  if (!state || !uploadManager) return null;
  const { removeUploadingFile, retryUpload, uploadingFiles } = uploadManager;
  const { setLocalFiles, setLocalImages } = state;
  const isMixedAttachment = localImages?.length > 0 && localFiles?.length > 0;

  const getFileUploadStatus = (clientId?: string) =>
    uploadingFiles.find((u) => u.file.clientId === clientId)?.status;

  const removeLocalFile = async (file: LocalFile) => {
    Keyboard.dismiss();
    const newLocalFiles = localFiles.filter((i) => i.id !== file.id);

    setLocalFiles(newLocalFiles);
    removeUploadingFile(file);
  };

  const removeLocalImage = async (file: LocalFile) => {
    Keyboard.dismiss();
    const newLocalImages = localImages.filter((i) => i.id !== file.id);

    setLocalImages(newLocalImages);
    removeUploadingFile(file);
  };

  // Shared by the thumbnail delete button and the image-preview screen's
  // own delete handler, so neither can remove an attachment mid-submit
  // (TA-10210 review).
  const guardedRemoveLocalImage = (file: LocalFile) => {
    if (!isReadySubmitting) removeLocalImage(file);
  };

  if (localFiles?.length + localImages?.length === 0) return null;

  return (
    <>
      {shouldShowLoader && localFiles?.length + localImages?.length > 0 && (
        <Box marginHorizontal='m' bg='grey01' padding='m'>
          <Text variant='bodyTaskName'>
            {`Uploading ${fileUploadedCount}/${
              localFiles?.length + localImages?.length
            } files`}{' '}
          </Text>
          <ProgressBar
            color={theme.colors.greenSecondary}
            style={{
              marginTop: theme.spacing.xxs,
              borderRadius: theme.borderRadii.xxs,
              height: theme.spacing.xs,
              backgroundColor: theme.colors.grey02,
            }}
            visible={shouldShowLoader}
            progress={
              fileUploadedCount / (localFiles?.length + localImages?.length)
            }
          />
        </Box>
      )}

      {!shouldShowLoader && localImages?.length > 0 && (
        <Box
          borderWidth={isMixedAttachment ? 1 : 0}
          marginHorizontal='m'
          marginBottom={isMixedAttachment ? 'xs' : 'm'}
          paddingHorizontal={isMixedAttachment ? 'xs' : 'none'}
          borderRadius='xs'
          borderColor='grey02'>
          <ChatImages
            numColumns={6}
            darkDeleteIcon
            // ChatImages derives its whole sizing mode from `!!onDelete`
            // (isChatInput), not just whether to show the delete badge —
            // passing undefined here to "disable" deletion during submit
            // flipped it into chat-message sizing math and visibly
            // resized the tiles. Keep onDelete always defined; guard the
            // actual removal internally instead.
            onDelete={guardedRemoveLocalImage}
            list={localImages}
            onPress={(image: Document | LocalFile) => {
              if (isReadySubmitting) return;
              navigation.navigate('chat-composer-image-preview', {
                images: localImages,
                initialIndex: localImages.findIndex(
                  (img) => img.id === image.id
                ),
                // Guarded the same way as the thumbnail delete handler —
                // the preview screen must not be able to delete
                // attachments mid-submit either (TA-10210 review).
                onDelete: guardedRemoveLocalImage,
              });
            }}
          />
        </Box>
      )}

      {!shouldShowLoader && localFiles?.length > 0 && (
        <Box
          mt='xs'
          borderWidth={isMixedAttachment ? 1 : 0}
          borderColor='grey02'
          borderRadius='xs'
          marginHorizontal='m'
          marginBottom='m'>
          <FlatList
            keyboardShouldPersistTaps='always'
            data={localFiles}
            scrollEnabled={false}
            keyExtractor={(item) => item.id}
            renderItem={({ item }) => (
              <Box
                style={{
                  marginTop: theme.spacing.xxs,
                }}>
                <DocumentCard
                  filterVal=''
                  showBorder={false}
                  document={{ ...item }}
                  darkDeleteIcon
                  onDelete={isReadySubmitting ? undefined : removeLocalFile}
                  status={getFileUploadStatus(item.clientId)}
                  onRetry={retryUpload}
                />
              </Box>
            )}
            ListFooterComponent={LocalFilesListFooter}
            ListHeaderComponent={LocalFilesListHeader}
          />
        </Box>
      )}
    </>
  );
};
